import { equals as equalsFn, pickAll } from 'ramda';

export const last = <T>(array: T[]): T | undefined => array?.[array.length - 1];

/**
 * @template U
 * @param {Array<U>} arrayA
 * @param {Array<U>} arrayB
 * @returns {Array<U>}
 */
export const arraySymmetricDiff = <T>(arrayA: T[] = [], arrayB: T[] = []): T[] =>
  arrayA.filter((x) => !arrayB.includes(x)).concat(arrayB.filter((x) => !arrayA.includes(x)));

/**
 * @template T
 * @typedef Options
 * @property {Array<T>} arrayA
 * @property {Array<T>} arrayB
 * @property {string} key
 * @property {(T, T) => boolean} testFunction
 */

interface ArrayDeepEqualOptions<T> {
  arrayA?: T[];
  arrayB?: T[];
  key?: string;
  testFunction?: (a: T, b: T) => boolean;
}

/**
 * @param {Options} options
 * @returns {boolean}
 */
export const arrayDeepEqual = <T extends Record<string, any>>({ arrayA = [], arrayB = [], key, testFunction }: ArrayDeepEqualOptions<T>): boolean =>
  arrayA.length === arrayB.length &&
  arrayA.every((item, index) => {
    let equals = arrayB[index] === item;

    if (!equals && key && arrayB[index] && item) {
      equals = arrayB[index][key] === item[key];
    }

    if (!equals && typeof testFunction === 'function') {
      equals = testFunction(item, arrayB[index]);
    }

    return equals;
  });

/**
 * @param {string} property
 * @param {Array<any>} items
 * @returns {{[field: any]: Array<any>}}
 */
export const groupBy = <T extends Record<string, any>>(property: string, items: T[] = []): Record<string, T[]> =>
  items.reduce(
    (res, item) => ({
      ...res,
      [item[property]]: [...(res[item[property]] || []), item]
    }),
    {} as Record<string, T[]>
  );

/**
 * Check if an element is before another element in an array
 * @template T
 * @param {Array<T>} array
 * @param {T} element
 * @param {T} referenceElement
 * @returns boolean
 */
export const isElementBefore = <T>(array: T[], element: T, referenceElement: T): boolean => {
  const index = array.indexOf(element);
  const referenceIndex = array.indexOf(referenceElement);

  return index < referenceIndex;
};

export const flatten = <T>(array: (T | T[])[]): T[] =>
  array.flatMap((value) => {
    if (Array.isArray(value)) {
      return flatten(value);
    }
    return value;
  });

/**
 * @param {any[]} array
 * @param {number} from
 * @param {number} to
 * @returns {any[]}
 */
export const moveElement = <T>(array: T[], from: number, to: number): T[] => {
  const arrayCopy = [...array];
  const element = arrayCopy.splice(from, 1)[0];

  arrayCopy.splice(to, 0, element);

  return arrayCopy;
};

type Falsy = null | undefined | 0 | '' | false;

export const isTruthy = <T>(value: T): value is Exclude<T, Falsy> => Boolean(value);

/**
 * @param {Array<Object>} data
 * @param {Array<string>} fields
 * @returns {boolean}
 */
export const compareObjects = (data: unknown[] = [], fields: string[] = []): boolean => {
  const items = data.map((item) => pickAll(fields, item));
  return items.every(item => equalsFn(item, items.at(0)));
};
